/**
 * Communications Center service, backed by the API.
 *
 * Deliberately thin: every call it makes already exists as a domain service, so
 * this composes them into the one boundary the modal talks to rather than
 * opening another `HttpClient`. The only work it does of its own is narrowing
 * the untyped client-search payload and assembling the dropdown lists.
 */

import type { ICommunicationService } from "../communication/ICommunicationService";
import type { ICommunicationManagementService } from "../communicationManagement/ICommunicationManagementService";
import type { IRefCommunicationTemplatesService } from "../refCommunicationTemplates/IRefCommunicationTemplatesService";
import { getAllTemplatePages } from "../refCommunicationTemplates/getAllTemplatePages";
import type { IRefOptionsService } from "../refOptions/IRefOptionsService";
import type { IRefServicesService } from "../refServices/IRefServicesService";
import type { IRefStoresService } from "../refStores/IRefStoresService";
import {
  CommunicationFilterOption,
  CommunicationFilterOptions,
  CommunicationSearchRequestOptions,
  CommunicationSend,
  CommunicationSendDelivery,
  CommunicationSendPageResponse,
  DeliveryFilters,
  DeliveryPageResponse,
  RecipientPageResponse,
  RecipientSearchParameters,
  ResponsePageResponse,
  SendCreateData,
  SendSearchParameters,
  TemplateListParameters,
  TemplatePageResponse,
  TemplateWriteData,
} from "@/types/communicationsCenter";
import { COMMUNICATION_TEMPLATE_TYPE_CODES } from "@/types/refCommunicationTemplate";
import { ICommunicationsCenterService } from "./ICommunicationsCenterService";
import { adaptRecipients } from "./adapters";

const byName = (a: CommunicationFilterOption, b: CommunicationFilterOption) =>
  a.name.localeCompare(b.name, "pt");

/**
 * The domain services this one is built from.
 *
 * Injected rather than imported from their barrels: those barrels resolve
 * through `ServiceFactory`, and `ServiceFactory` constructs this class, so
 * importing them directly forms a cycle that leaves the factory undefined at
 * module-evaluation time.
 */
export interface CommunicationsCenterDependencies {
  communicationService: ICommunicationService;
  communicationManagementService: ICommunicationManagementService;
  refCommunicationTemplatesService: IRefCommunicationTemplatesService;
  refServicesService: IRefServicesService;
  refOptionsService: IRefOptionsService;
  refStoresService: IRefStoresService;
}

export class CommunicationsCenterService implements ICommunicationsCenterService {
  private readonly communicationService: ICommunicationService;
  private readonly communicationManagementService: ICommunicationManagementService;
  private readonly refCommunicationTemplatesService: IRefCommunicationTemplatesService;
  private readonly refServicesService: IRefServicesService;
  private readonly refOptionsService: IRefOptionsService;
  private readonly refStoresService: IRefStoresService;

  constructor(dependencies: CommunicationsCenterDependencies) {
    this.communicationService = dependencies.communicationService;
    this.communicationManagementService =
      dependencies.communicationManagementService;
    this.refCommunicationTemplatesService =
      dependencies.refCommunicationTemplatesService;
    this.refServicesService = dependencies.refServicesService;
    this.refOptionsService = dependencies.refOptionsService;
    this.refStoresService = dependencies.refStoresService;
  }

  async searchSends(
    parameters: SendSearchParameters,
    options: CommunicationSearchRequestOptions = {},
  ): Promise<CommunicationSendPageResponse> {
    return this.communicationService.search(parameters, options);
  }

  async searchDeliveries(
    communicationId: number,
    parameters: DeliveryFilters = {},
    options: CommunicationSearchRequestOptions = {},
  ): Promise<DeliveryPageResponse> {
    return this.communicationService.searchDeliveries(
      communicationId,
      parameters,
      options,
    );
  }

  async searchResponses(
    communicationId: number,
    options: CommunicationSearchRequestOptions = {},
  ): Promise<ResponsePageResponse> {
    return this.communicationService.searchResponses(communicationId, options);
  }

  async getDeliveryStatusCodes(): Promise<string[]> {
    return this.communicationService.getDeliveryStatusCodes();
  }

  async getStatusCodes(): Promise<string[]> {
    return this.communicationService.getStatusCodes();
  }

  async listTemplates(
    parameters: TemplateListParameters = {},
  ): Promise<TemplatePageResponse> {
    const { search, page, ...rest } = parameters;

    const options = {
      ...rest,
      /*
       * External templates are owned by another system: the backoffice neither
       * lists them nor offers them for a send. Defaulted rather than forced, so
       * a caller that genuinely wants them can still ask.
       */
      isExternal: parameters.isExternal ?? false,
      size: parameters.size ?? 100,
    };
    const response = await getAllTemplatePages(
      this.refCommunicationTemplatesService,
      options,
    );

    /*
     * Without a type, the list is what the backoffice authors: generic and
     * product-reminder models. Follow-up models belong to the appointment
     * flow — `FollowUpModal` offers them — so they are dropped here; the API
     * filters by one type only. All pages are loaded before dropping them.
     */
    const authored =
      parameters.typeCode === undefined
        ? response.content.filter(
            template =>
              template.typeCode !== COMMUNICATION_TEMPLATE_TYPE_CODES.FOLLOW_UP,
          )
        : response.content;

    /*
     * The endpoint has no text search, so name/subject matching is done here.
     * The complete list has been loaded above before filtering or pagination.
     */
    const needle = search?.trim().toLowerCase();
    const content = needle
      ? authored.filter(template =>
          `${template.name} ${template.subject}`.toLowerCase().includes(needle),
        )
      : authored;

    if (page === undefined) {
      return {
        content,
        page: {
          size: content.length,
          number: 0,
          totalElements: content.length,
          totalPages: 1,
        },
      };
    }

    const size = parameters.size ?? 100;
    const start = page * size;
    return {
      content: content.slice(start, start + size),
      page: {
        size,
        number: page,
        totalElements: content.length,
        totalPages: Math.max(1, Math.ceil(content.length / size)),
      },
    };
  }

  async createTemplate(templateData: TemplateWriteData): Promise<number> {
    return this.refCommunicationTemplatesService.create(templateData);
  }

  async updateTemplate(
    id: number,
    templateData: TemplateWriteData,
  ): Promise<void> {
    return this.refCommunicationTemplatesService.update(id, templateData);
  }

  async deleteTemplate(id: number): Promise<void> {
    return this.refCommunicationTemplatesService.delete(id);
  }

  async searchRecipients(
    parameters: RecipientSearchParameters,
    options: CommunicationSearchRequestOptions = {},
  ): Promise<RecipientPageResponse> {
    const response = await this.communicationService.searchClients(
      parameters,
      options,
    );

    return {
      content: adaptRecipients(response.content),
      page: response.page,
    };
  }

  /**
   * Send a communication written in the backoffice to its whole audience.
   *
   * A bulk send carries the audience filters, not a contact list: the API
   * resolves the clients itself, in the background, and answers at once with
   * the communication `INITIALIZING`. It has no `isFollowUp` — a bulk send is
   * never one, and the API refuses a follow-up template.
   */
  async createSend(sendData: SendCreateData): Promise<CommunicationSend> {
    return this.communicationManagementService.createBulk(sendData);
  }

  async resendSend(
    communicationId: number,
    includeSkipped = false,
  ): Promise<CommunicationSend> {
    return this.communicationManagementService.resend(
      communicationId,
      includeSkipped,
    );
  }

  async resendToClient(
    communicationId: number,
    clientId: number,
  ): Promise<CommunicationSendDelivery> {
    return this.communicationManagementService.resendToClient(
      communicationId,
      clientId,
    );
  }

  async cancelSend(communicationId: number): Promise<void> {
    return this.communicationManagementService.cancel(communicationId);
  }

  async cancelDelivery(deliveryId: number): Promise<void> {
    return this.communicationManagementService.cancelDelivery(deliveryId);
  }

  /**
   * The dropdown lists, gathered in parallel.
   *
   * `allSettled` on purpose: these are filters, not required input, so one list
   * failing leaves the others usable and that dropdown simply offers "all".
   * Template loading accepts an abort signal; the other reference services
   * settle before the caller's signal is checked.
   */
  async getFilterOptions(
    signal?: AbortSignal,
  ): Promise<CommunicationFilterOptions> {
    const [templates, services, serviceTypes, species, stores] =
      await Promise.allSettled([
        /* Internal, generic templates only: these dropdowns pick the model
           of a send, and a send can use neither a follow-up nor a
           product-reminder model. */
        getAllTemplatePages(this.refCommunicationTemplatesService, {
          size: 100,
          isExternal: false,
          typeCode: COMMUNICATION_TEMPLATE_TYPE_CODES.GENERIC,
          signal,
        }),
        this.refServicesService.getAll(),
        this.refOptionsService.getOptionsByCode("SERVICE_TYPE"),
        this.refOptionsService.getOptionsByCode("SPECIES"),
        this.refStoresService.getAllHubs(),
      ]);

    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    return {
      templates:
        templates.status === "fulfilled" ? (templates.value.content ?? []) : [],
      services:
        services.status === "fulfilled"
          ? (services.value.content ?? [])
              .map(service => ({ id: service.id, name: service.name }))
              .sort(byName)
          : [],
      serviceTypes:
        serviceTypes.status === "fulfilled"
          ? serviceTypes.value
              .map(option => ({ id: option.id, name: option.value }))
              .sort(byName)
          : [],
      species:
        species.status === "fulfilled"
          ? species.value
              .map(option => ({ id: option.id, name: option.value }))
              .sort(byName)
          : [],
      stores:
        stores.status === "fulfilled"
          ? (stores.value.content ?? [])
              .map(store => ({ id: store.id, name: store.name }))
              .sort(byName)
          : [],
    };
  }
}
